/* ============================================================================
   popup-system.js - controller for the theme-native popup system.
   Loaded deferred by sections/popup-system.liquid.

   Responsibilities: unit targeting (path exclusions, UTM gating, A/B), the
   scroll+dwell engagement gate, teaser, modal + step engine, Klaviyo client
   subscribe (email + SMS) with validation and real error handling,
   suppression, desktop exit intent for the cart unit, discount auto-apply
   redirects, and dataLayer events.

   Oct 2026 (Alia learnings): offer-first routing, quiz for returning
   non-subscribers, one-tap first step, time fallback trigger, popup gap,
   reopen pill, site-wide exit offer, SMS upgrade unit for email subscribers.

   Popups v2 (25 Oct 2026): welcome A/B (mystery vs find your match), texture
   picks, hair_texture to Klaviyo, human-input filter before any automatic
   open, impression fired on reveal plus a once-per-session view_unique event.

   Preview: ?pl_popup=<unit> forces a unit open, bypassing gates and
   suppression; its events carry preview:true so analytics can exclude them.

   Note on suppression durability: suppression is stored in localStorage and
   mirrored to a cookie. Safari/iOS ITP clears script-writable storage after
   7 days without a first-party interaction, so suppression windows longer
   than 7 days will not always hold on those browsers. Expect some
   over-showing on iOS; this is a browser constraint, not a bug here.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-plps-root]');
  var editorUnit = null;
  if (!root) return;

  var cfgEl = root.querySelector('[data-plps-config]');
  var cfg;
  try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }

  var KLAVIYO_REVISION = '2026-07-15';

  var LS = {
    get: function (k) { try { return localStorage.getItem('plps:' + k); } catch (e) { return null; } },
    set: function (k, v) { try { localStorage.setItem('plps:' + k, v); } catch (e) {} }
  };

  /* Cookie mirror - covers browsers where localStorage is unavailable
     (private modes, blocked storage). Not a fix for ITP expiry. */
  var CK = {
    get: function (k) {
      var m = document.cookie.match('(?:^|; )plps_' + k.replace(/[^a-z0-9_]/gi, '') + '=([^;]*)');
      return m ? decodeURIComponent(m[1]) : null;
    },
    set: function (k, v, days) {
      try {
        document.cookie = 'plps_' + k.replace(/[^a-z0-9_]/gi, '') + '=' + encodeURIComponent(v) +
          ';path=/;max-age=' + Math.round(days * 86400) + ';SameSite=Lax';
      } catch (e) {}
    }
  };

  /* ---------- visitor state ---------- */
  var visitorId = LS.get('vid') || CK.get('vid');
  if (!visitorId) {
    visitorId = 'v' + Math.random().toString(36).slice(2) + Date.now().toString(36);
  }
  LS.set('vid', visitorId);
  CK.set('vid', visitorId, 365);

  // Session counting: one increment per browser session.
  var sessions = parseInt(LS.get('sessions') || '0', 10);
  var sessionMarked = false;
  try { sessionMarked = sessionStorage.getItem('plps:session') === '1'; } catch (e) {}
  if (!sessionMarked) {
    sessions += 1;
    LS.set('sessions', String(sessions));
    try { sessionStorage.setItem('plps:session', '1'); } catch (e) {}
  }

  var isStylist = LS.get('stylist') === '1';
  /* A logged-in customer with past orders is never a "new visitor", whatever
     this browser's storage says. Without this, existing customers on a new
     device or after an iOS storage purge were served the welcome offer. */
  var isSubscribed = LS.get('subscribed') === '1' || CK.get('subscribed') === '1' || cfg.knownCustomer === true;
  var params = new URLSearchParams(location.search);
  var previewUnit = params.get('pl_popup');
  var isPreview = !!previewUnit;
  var inDesignMode = !!(window.Shopify && window.Shopify.designMode);

  /* PL 2026-10-20: automated browsers never see or count popups. Headless
     crawlers inflated impressions (about 29% of GA4 popup impressions on
     Oct 14-15 were Asian desktop bots) and made conversion rates look worse
     than they are. Preview and the theme editor are never filtered. */
  if (!isPreview && !inDesignMode) {
    var plpsUa = navigator.userAgent || '';
    if (navigator.webdriver === true ||
        /HeadlessChrome|Lighthouse|PageSpeed|bot\/|crawler|spider|facebookexternalhit|AdsBot/i.test(plpsUa)) return;
  }

  /* PL 2026-10-25 popups v2: human-input filter. No automatic popup opens
     until this page has seen a real pointer move or press, a touch, a key
     press or a wheel turn. Simple bots fire none of these, and they made up
     most desktop "views" in October (7,339 from Virginia data centers in 12
     days with 48 closes). It is not a guarantee: a scripted browser can fake
     input. Klaviyo list adds stay the measure of success. Preview, the theme
     editor and the reopen pill (a click) are never held. */
  var humanSeen = false;
  var humanWaiters = [];
  var HUMAN_EVENTS = ['pointermove', 'pointerdown', 'touchstart', 'keydown', 'wheel'];
  function onHuman() {
    if (humanSeen) return;
    humanSeen = true;
    HUMAN_EVENTS.forEach(function (t) { window.removeEventListener(t, onHuman, true); });
    humanWaiters.splice(0).forEach(function (fn) { try { fn(); } catch (err) {} });
  }
  HUMAN_EVENTS.forEach(function (t) { window.addEventListener(t, onHuman, { capture: true, passive: true }); });
  function whenHuman(fn) {
    if (humanSeen || isPreview || inDesignMode) fn();
    else humanWaiters.push(fn);
  }

  /* ---------- first-touch UTM capture (for per-unit UTM gating) ---------- */
  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  var sessionUtm = {};
  try {
    var storedUtm = sessionStorage.getItem('plps:utm');
    if (storedUtm) sessionUtm = JSON.parse(storedUtm) || {};
  } catch (e) { sessionUtm = {}; }
  var sawUtmNow = false;
  UTM_KEYS.forEach(function (k) {
    var v = params.get(k);
    if (v) { sessionUtm[k] = v.toLowerCase(); sawUtmNow = true; }
  });
  if (sawUtmNow) {
    try { sessionStorage.setItem('plps:utm', JSON.stringify(sessionUtm)); } catch (e) {}
  }

  /* ---------- suppression ---------- */
  /* The promo unit scopes its dismissal memory to its promo key, so a new
     promo (new key) re-arms for everyone, including people who dismissed the
     last one. Every other unit keys on its own name, unchanged. */
  function supKey(unit) {
    if (unit === 'promo' && cfg.units.promo && cfg.units.promo.promoKey) {
      return 'promo.' + String(cfg.units.promo.promoKey).replace(/[^a-zA-Z0-9_-]/g, '');
    }
    return unit;
  }
  function suppressedUntil(unit) {
    var k = supKey(unit);
    var a = parseInt(LS.get('sup:' + k) || '0', 10);
    var b = parseInt(CK.get('sup_' + k) || '0', 10);
    return Math.max(isNaN(a) ? 0 : a, isNaN(b) ? 0 : b);
  }
  function suppressed(unit) {
    return Date.now() < suppressedUntil(unit);
  }
  function suppress(unit) {
    if (!unit) return;
    var days = unit === 'exit_offer'
      ? (cfg.exitOfferDays || 5)
      : ((cfg.units[unit] && cfg.units[unit].suppressDays) || 7);
    var until = Date.now() + days * 86400000;
    var k = supKey(unit);
    LS.set('sup:' + k, String(until));
    CK.set('sup_' + k, String(until), days);
  }

  /* Past its configured end date (visitor local time, end of day), the promo
     unit stands down automatically even if the block is still enabled - the
     forgotten-promo safety net. Blank or malformed dates never expire. */
  function promoExpired() {
    var u = cfg.units.promo;
    if (!u || !u.endDate) return false;
    var t = Date.parse(String(u.endDate) + 'T23:59:59');
    return !isNaN(t) && Date.now() > t;
  }

  function markSubscribed() {
    isSubscribed = true;
    LS.set('subscribed', '1');    CK.set('subscribed', '1', 365);
    hidePill(true);
  }

  /* PL 2026-10-13: remembers that this browser gave a phone number, so the
     SMS upgrade unit never asks again. */
  function markSms() {
    LS.set('sms', '1'); CK.set('sms', '1', 365);
    hidePill(true);
  }
  function hasSms() { return LS.get('sms') === '1' || CK.get('sms') === '1'; }

  /* PL 2026-10-13 popup gap: no automatic popup within N minutes of the last
     one, across pages (Alia used 5-10 minutes). Preview and editor opens
     never count. */
  function recentlyOpened() {
    if (!cfg.popupGapMs) return false;
    var t = parseInt(LS.get('last_open') || '0', 10);
    return !isNaN(t) && t > 0 && (Date.now() - t) < cfg.popupGapMs;
  }

  /* PL 2026-10-13 reopen pill. After a welcome unit is closed without a
     signup, a small "claim" pill stays bottom-left for the rest of the
     session (sessionStorage), on every page, until the visitor signs up or
     hides it with its own close button. Clicking it reopens the unit. */
  var PILL_UNITS = { new_visitor: 1, quiz: 1, returning: 1, game: 1, sms_upgrade: 1 };
  var pill = root.querySelector('[data-plps-pill]');
  function pillAllowed(unit) {
    if (!cfg.reopenPill || !pill || !PILL_UNITS[unit]) return false;
    if (!root.querySelector('[data-plps-unit="' + unit + '"]') || !cfg.units[unit]) return false;
    if (unit === 'sms_upgrade') return !hasSms();
    return !isSubscribed;
  }
  function readPill() {
    try { return JSON.parse(sessionStorage.getItem('plps:pill') || 'null'); } catch (e) { return null; }
  }
  function showPill(unit, variant) {
    if (isPreview || inDesignMode || !pillAllowed(unit)) return;
    if (!pathAllowed(unit)) return;
    try { sessionStorage.setItem('plps:pill', JSON.stringify({ unit: unit, variant: variant || '' })); } catch (e) {}
    pill.setAttribute('data-unit', unit);
    pill.setAttribute('data-variant', variant || '');
    pill.hidden = false;
    pill.classList.add('is-visible');
  }
  function hidePill(forget) {
    if (!pill) return;
    pill.classList.remove('is-visible');
    pill.hidden = true;
    if (forget) { try { sessionStorage.removeItem('plps:pill'); } catch (e) {} }
  }

  /* ---------- analytics ---------- */
  function track(event, data) {
    window.dataLayer = window.dataLayer || [];
    var payload = {
      event: 'plps_' + event,
      plps_unit: state.unit || '',
      plps_variant: state.variant || '',
      plps_preview: (isPreview || inDesignMode)
    };
    if (data) for (var k in data) payload['plps_' + k] = data[k];
    window.dataLayer.push(payload);
  }

  /* ---------- consent (optional) ----------
     When "Require marketing consent" is on, the popup will not open and no
     Klaviyo call is made until Shopify's Customer Privacy API reports
     marketing consent. If the API never loads, nothing shows - that is the
     conservative behaviour and it is intentional. */
  /* Is a consent banner currently on screen?
     Two signals, either one counts:
       1. Shopify's Customer Privacy API says the banner should be showing for
          this visitor (region requires it and nothing has been collected yet).
       2. A Pandectes banner element is actually visible in the DOM. This is
          the fallback for regions like the US where marketingAllowed() is true
          by default (opt-out model) so the API alone would let the popup open
          on top of the banner - which is exactly what we are preventing. */
  function consentBannerVisible() {
    var cp = window.Shopify && window.Shopify.customerPrivacy;
    if (cp && typeof cp.shouldShowBanner === 'function') {
      /* PL 2026-10-11: shouldShowBanner() means "this region requires a banner",
         not "the banner is up right now". In California and Colorado it stays
         true after the visitor has already answered, which made the popup wait
         forever. Treat the banner as pending only while no marketing choice has
         been recorded; once Accept or Decline is saved, consentOk() reads the
         real answer from marketingAllowed(). */
      try {
        if (cp.shouldShowBanner()) {
          var given = typeof cp.currentVisitorConsent === 'function' ? cp.currentVisitorConsent() : null;
          var choice = given && given.marketing;
          if (choice !== 'yes' && choice !== 'no') return true;
        }
      } catch (e) {}
    }
    var nodes = document.querySelectorAll('[id*="pandectes"], [class*="pandectes"]');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var hint = (el.id + ' ' + el.className).toLowerCase();
      if (hint.indexOf('banner') === -1 && hint.indexOf('modal') === -1) continue;
      if (el.hidden) continue;
      var cs = window.getComputedStyle(el);
      if (cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity) === 0) continue;
      var r = el.getBoundingClientRect();
      if (r.height > 0 && r.width > 0) return true;
    }
    return false;
  }

  /* ---------- CMP dismissal watcher ----------
     The Pandectes banner can be closed two ways: a real choice (Accept /
     Decline / Preferences), which fires Shopify's visitorConsentCollected,
     or the X / Escape, which fires nothing. An X-press means "not now" - so
     it suppresses the popup for the rest of the session. Detection: the
     banner was visible, then is not, and no consent event arrived within a
     grace window. The flag is sessionStorage, so a new tab or tomorrow's
     visit starts clean, matching the banner's own reappearance. */
  var CMP_DISMISS_KEY = 'plps:cmp-dismissed';
  function cmpDismissed() {
    try { return sessionStorage.getItem(CMP_DISMISS_KEY) === '1'; } catch (e) { return false; }
  }
  (function watchCmpDismiss() {
    if (!cfg.requireConsent) return;
    var sawBanner = false;
    var lastChoiceAt = 0;
    var settled = false;
    var startedAt = Date.now();
    document.addEventListener('visitorConsentCollected', function () { lastChoiceAt = Date.now(); });
    (function poll() {
      if (settled) return;
      if (consentBannerVisible()) {
        sawBanner = true;
        setTimeout(poll, 500);
        return;
      }
      if (!sawBanner) {
        /* Banner never rendered (stored consent, or region without one).
           Keep watching briefly in case it mounts late, then stand down. */
        if (Date.now() - startedAt > 30000) { settled = true; return; }
        setTimeout(poll, 500);
        return;
      }
      /* Visible -> gone. Give the consent event time to arrive before
         concluding this was a bare dismissal. */
      settled = true;
      setTimeout(function () {
        if (Date.now() - lastChoiceAt < 2500) return; // Accept/Decline/Prefs
        /* PL 2026-10-20 (section setting, off by default): where marketing is
           allowed by default (opt-out regions), a bare close no longer hides
           popups for the session. Strict regions report marketingAllowed()
           false until Accept, so they are unaffected either way. */
        if (cfg.optOutShowAfterDismiss) {
          var cpx = window.Shopify && window.Shopify.customerPrivacy;
          try { if (cpx && typeof cpx.marketingAllowed === 'function' && cpx.marketingAllowed()) return; } catch (e) {}
        }
        try { sessionStorage.setItem(CMP_DISMISS_KEY, '1'); } catch (e) {}
      }, 1200);
    })();
  })();

  /* Resolves true only when BOTH hold:
       - marketingAllowed() is true (Pandectes writes into Shopify's API), and
       - no consent banner is on screen.
     The API-readiness wait is capped at 6s and fails closed. The banner wait
     is NOT capped: if the visitor never clears the banner, the popup never
     opens on this pageview. visitorConsentCollected short-circuits the poll
     the moment they accept or decline.
     opts.ignoreBanner: API-only check, used by the form submit paths so a
     visitor who is actively submitting (preview mode can open a unit while a
     banner is up) is never left waiting on the banner. */
  function consentOk(opts) {
    if (!cfg.requireConsent) return Promise.resolve(true);
    var ignoreBanner = !!(opts && opts.ignoreBanner);
    return new Promise(function (resolve) {
      var start = Date.now();
      var settled = false;
      function finish(v) {
        if (settled) return;
        settled = true;
        document.removeEventListener('visitorConsentCollected', onConsent);
        resolve(v);
      }
      var bannerWasSeen = false;
      var bannerSettleWaited = false;
      function check() {
        if (settled) return;
        if (!ignoreBanner && cmpDismissed()) { finish(false); return; }
        var cp = window.Shopify && window.Shopify.customerPrivacy;
        var apiReady = cp && typeof cp.marketingAllowed === 'function';
        if (!apiReady) {
          if (Date.now() - start > 6000) { finish(false); return; }
          setTimeout(check, 400);
          return;
        }
        var allowed = false;
        try { allowed = !!cp.marketingAllowed(); } catch (e) { allowed = false; }
        /* PL 2026-10-20 (section setting, off by default): in opt-out regions
           (marketing allowed by default and Shopify reports no banner is
           required) the Pandectes notice is informational, and ~95% of
           visitors never touch it. With the setting on, the popup no longer
           waits for it there. Strict regions (California, EU, UK) report
           marketingAllowed() false until Accept, so they never reach this
           branch and keep waiting for a real choice. */
        var optOutRegion = !!(cfg.optOutShowAfterDismiss && allowed);
        if (!ignoreBanner && !optOutRegion && consentBannerVisible()) { bannerWasSeen = true; setTimeout(check, 500); return; }
        if (!ignoreBanner && bannerWasSeen && !bannerSettleWaited) {
          /* The banner just cleared. Wait out the dismissal watcher's grace
             window so an X-press is flagged before we conclude "allowed". */
          bannerSettleWaited = true;
          setTimeout(check, 2000);
          return;
        }
        if (allowed) { finish(true); return; }
        // Not allowed and no banner: they declined, or region blocks by default.
        finish(false);
      }
      function onConsent() { setTimeout(check, 50); }
      document.addEventListener('visitorConsentCollected', onConsent);
      check();
    });
  }

  /* ---------- known-subscriber check ----------
     Klaviyo identifies a profile when someone arrives from an email or has
     submitted a form on this browser before. Used only to skip gate units. */
  function klaviyoIdentified() {
    if (!cfg.respectKlaviyoIdentified) return Promise.resolve(false);
    return new Promise(function (resolve) {
      var start = Date.now();
      (function poll() {
        var k = window.klaviyo;
        if (k && typeof k.isIdentified === 'function') {
          try {
            var r = k.isIdentified();
            if (r && typeof r.then === 'function') {
              r.then(function (v) { resolve(!!v); }).catch(function () { resolve(false); });
            } else {
              resolve(!!r);
            }
          } catch (e) { resolve(false); }
          return;
        }
        if (Date.now() - start > 3000) { resolve(false); return; }
        setTimeout(poll, 300);
      })();
    });
  }

  /* Resolved once at boot and read inside the dwell wait. Defaults to a
     settled `false` so the preview and theme-editor paths never wait on it. */
  var identifiedPromise = Promise.resolve(false);

  /* ---------- path / UTM eligibility ---------- */
  function pathExcluded() {
    var path = location.pathname.toLowerCase();
    var list = cfg.excludedPaths || [];
    for (var i = 0; i < list.length; i++) {
      var p = String(list[i] || '').trim().toLowerCase();
      if (!p) continue;
      if (path.indexOf(p) === 0) return true;
    }
    return false;
  }

  function onCartPage() {
    return cfg.pageType === 'cart' || location.pathname.replace(/\/$/, '') === '/cart';
  }

  // Gate units (1-4) never show on the cart page or on excluded paths.
  // The cart-exit unit is exempt from the cart-page rule only.
  function pathAllowed(unit) {
    if (pathExcluded()) return false;
    if (unit !== 'cart_exit' && onCartPage()) return false;
    return true;
  }

  function utmAllowed(unit) {
    var rule = (cfg.units[unit] && cfg.units[unit].onlyUtm) || '';
    rule = String(rule).trim();
    if (!rule) return true;
    var clauses = rule.split(',');
    for (var i = 0; i < clauses.length; i++) {
      var pair = clauses[i].split('=');
      if (pair.length !== 2) continue;
      var key = pair[0].trim().toLowerCase();
      var val = pair[1].trim().toLowerCase();
      if (!key || !val) continue;
      var current = (params.get(key) || sessionUtm[key] || '').toLowerCase();
      if (current === val) return true;
    }
    return false;
  }

  /* ---------- input validation ---------- */
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i;

  function validEmail(v) {
    return EMAIL_RE.test(String(v || '').trim());
  }

  // Klaviyo requires E.164. Normalise common US formats and anything the
  // visitor types with a leading +. Returns null when it cannot be trusted.
  function normalizePhone(raw) {
    var v = String(raw || '').trim();
    if (!v) return null;
    var hasPlus = v.charAt(0) === '+';
    var digits = v.replace(/\D/g, '');
    if (!digits) return null;
    if (hasPlus) {
      return (digits.length >= 8 && digits.length <= 15) ? '+' + digits : null;
    }
    var cc = String(cfg.defaultCountryCode || '+1').replace(/\D/g, '') || '1';
    if (cc === '1') {
      if (digits.length === 11 && digits.charAt(0) === '1') return '+' + digits;
      if (digits.length === 10) return '+1' + digits;
      return null;
    }
    if (digits.indexOf(cc) === 0 && digits.length >= 10) return '+' + digits;
    if (digits.length >= 6 && digits.length <= 13) return '+' + cc + digits.replace(/^0+/, '');
    return null;
  }

  /* ---------- order attribution ----------
     Stamps the test arm onto the Shopify cart as cart attributes, which
     carry through to the order (Additional details in admin, and
     customAttributes in the Admin API). This makes Shopify, not GA4, the
     record of which arm produced which order. Arms A and B share the same
     discount codes, so code usage alone cannot tell them apart. Best effort:
     never blocks or delays the popup flow. */
  function stampCart() {
    if (isPreview || inDesignMode || !state.variant) return;
    try {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: {
          plps_variant: state.variant,
          plps_unit: state.unit || '',
          plps_tier: state.smsCode ? 'sms' : 'email'
        } })
      }).catch(function () {});
    } catch (e) {}
  }

  /* ---------- Klaviyo client subscribe ---------- */
  function subscribe(listId, attrs, properties, source) {
    var profileAttrs = {};
    if (attrs.email) profileAttrs.email = attrs.email;
    if (attrs.phone) profileAttrs.phone_number = attrs.phone;
    if (properties && Object.keys(properties).length) profileAttrs.properties = properties;
    // DO NOT add a `subscriptions` object here. It was tried on 20 Aug 2026 and
    // the endpoint rejected it outright at revision 2024-10-15:
    //   400 invalid - "'subscriptions' is not a valid field"
    //   pointer /data/attributes/profile/data/attributes/subscriptions
    // That failure broke every submit, email included. This endpoint derives
    // the consent record from the presence of email / phone_number. Verify SMS
    // consent by inspecting the profile's SMS channel in Klaviyo after a real
    // opt-in, not by reasoning from the Subscribe Profiles docs, which describe
    // a different (server-side) endpoint.
    var body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: source,
          profile: { data: { type: 'profile', attributes: profileAttrs } }
        },
        relationships: { list: { data: { type: 'list', id: listId } } }
      }
    };
    return fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(cfg.klaviyoCompanyId), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'revision': KLAVIYO_REVISION },
      body: JSON.stringify(body)
    }).then(function (res) {
      // fetch resolves on 4xx/5xx, so this check is what makes failures real.
      if (!res.ok) {
        var err = new Error('Klaviyo responded ' + res.status);
        err.status = res.status;
        throw err;
      }
      return res;
    });
  }

  /* Without this the browser stays anonymous to Klaviyo onsite tracking, so
     browse-abandon and Active-on-Site never attach to the profile that just
     subscribed. This is the single biggest gap versus a hosted popup tool. */
  function klaviyoIdentify(attrs) {
    try {
      var k = window.klaviyo;
      if (!k) return;
      var payload = {};
      if (attrs.email) payload.email = attrs.email;
      if (attrs.phone) payload.phone_number = attrs.phone;
      if (!Object.keys(payload).length) return;
      if (typeof k.identify === 'function') { k.identify(payload); }
      else if (typeof k.push === 'function') { k.push(['identify', payload]); }
    } catch (e) {}
  }

  /* Back-in-stock alert for the abandoned variant. The cart-exit unit promises
     "we'll tell you before it's gone", and nothing else in this file keeps that
     promise. Variant id format matches assets/klaviyo-bis.js. */
  function backInStock(variantId, attrs, channels) {
    if (!variantId || (!attrs.email && !attrs.phone)) return Promise.resolve();
    var profileAttrs = {};
    if (attrs.email) profileAttrs.email = attrs.email;
    if (attrs.phone) profileAttrs.phone_number = attrs.phone;
    return fetch('https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' + encodeURIComponent(cfg.klaviyoCompanyId), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'revision': KLAVIYO_REVISION },
      body: JSON.stringify({
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            channels: channels || ['EMAIL'],
            profile: { data: { type: 'profile', attributes: profileAttrs } }
          },
          relationships: {
            variant: { data: { type: 'catalog-variant', id: '$shopify:::$default:::' + variantId } }
          }
        }
      })
    }).then(function (res) {
      if (!res.ok) throw new Error('BIS responded ' + res.status);
    }).catch(function (err) {
      // Never block the success step on this; surface it for debugging only.
      track('bis_error', { reason: (err && err.message) || 'unknown' });
    });
  }

  function klaviyoEvent(metricName, email, properties) {
    // Off by default: every new metric consumes Klaviyo metric capacity.
    // Goal data is written to the profile as `hair_goal` regardless.
    if (!cfg.trackGoalEvent || !email) return Promise.resolve();
    return fetch('https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(cfg.klaviyoCompanyId), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'revision': KLAVIYO_REVISION },
      body: JSON.stringify({
        data: {
          type: 'event',
          attributes: {
            metric: { data: { type: 'metric', attributes: { name: metricName } } },
            profile: { data: { type: 'profile', attributes: { email: email } } },
            properties: properties || {}
          }
        }
      })
    }).catch(function () {});
  }

  /* ---------- cart summary (for cart_exit) ---------- */
  function getCart() {
    // plCart is a display-only summary from footer-scripts; if it lacks the
    // variant id we need for back-in-stock, fall through to /cart.js.
    // PL 2026-10-27: also trust a fresh empty summary so empty-cart visitors skip /cart.js (it triggers the ~770KB checkout prewarm); needs footer-scripts to set plCart.stale.
    if (window.plCart && !window.plCart.stale && window.plCart.count >= 0 && window.plCart.firstItem !== undefined && (window.plCart.variantId || window.plCart.count === 0)) {
      return Promise.resolve(window.plCart);
    }
    return fetch('/cart.js').then(function (r) { return r.json(); }).then(function (c) {
      var first = (c.items && c.items[0]) || null;
      return {
        firstItem: first ? (first.product_title + (first.variant_title && first.variant_title !== 'Default Title' ? ' - ' + first.variant_title : '')) : '',
        count: c.item_count || 0,
        image: first ? first.image : '',
        qty: first ? first.quantity : 0,
        variantId: first ? String(first.variant_id) : '',
        productId: first ? String(first.product_id) : '',
        productTitle: first ? first.product_title : '',
        variantTitle: (first && first.variant_title && first.variant_title !== 'Default Title') ? first.variant_title : '',
        url: first ? first.url : ''
      };
    }).catch(function () { return { firstItem: '', count: 0 }; });
  }

  /* ---------- modal + step engine ---------- */
  var state = { unit: null, variant: '', email: '', goal: '', goalUrl: '', smsCode: '', open: false, cart: null, arm: '', texture: '', textureUrl: '' };

  /* PL 2026-10-25 popups v2: the welcome unit runs two first screens, A
     "mystery" (one tap, then email) and B "match" (texture, picks, email).
     The arm is deterministic on the visitor id (salted so it does not line up
     with the old intro roll), so a visitor always sees the same version,
     including when the reopen pill brings it back. ?pl_arm= forces one for
     previews; the theme editor uses the block's "Version shown" setting. */
  function welcomeArm() {
    var u = cfg.units.new_visitor || {};
    var forced = params.get('pl_arm');
    if (forced === 'mystery' || forced === 'match') return forced;
    if (inDesignMode) return u.editorArm === 'match' ? 'match' : 'mystery';
    var split = typeof u.armSplit === 'number' ? u.armSplit : 50;
    var key = 'welcome2:' + visitorId, h = 0;
    for (var i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
    return (h % 100) < split ? 'mystery' : 'match';
  }

  /* PL 2026-10-25 popups v2: texture pick (tapped, or read from the page).
     Shows that texture's three picks on the next step, echoes the label as
     its kicker, and sends the visitor to that texture's collection when they
     redeem. Returns the step to show next. */
  function applyTexture(btn) {
    state.texture = btn.getAttribute('data-texture') || '';
    state.textureUrl = btn.getAttribute('data-url') || '';
    state.goalUrl = state.textureUrl;
    var nextStep = btn.getAttribute('data-next') || 'email';
    var label = btn.querySelector('b') ? btn.querySelector('b').textContent : '';
    var stepEl = modal.querySelector('[data-plps-step="' + nextStep + '"]');
    if (stepEl) {
      var texEcho = stepEl.querySelector('[data-plps-texture-echo]');
      if (texEcho && label) texEcho.textContent = label;
      modal.querySelectorAll('[data-plps-picks]').forEach(function (p) {
        p.hidden = p.getAttribute('data-plps-picks') !== state.texture || !p.children.length;
      });
    }
    return nextStep;
  }

  /* Make a step the opening one without the focus move or step event that
     showStep() fires - used before the modal is revealed. */
  function setInitialStep(name) {
    var target = modal && modal.querySelector('[data-plps-step="' + name + '"]');
    if (!target) return;
    var steps = modal.querySelectorAll('[data-plps-step]');
    for (var i = 0; i < steps.length; i++) steps[i].classList.toggle('is-active', steps[i] === target);
  }
  var overlay, modal, teaser = root.querySelector('[data-plps-teaser]');
  var lastFocus = null;
  var scrollLockY = 0;
  var titleSeq = 0;

  function lockScroll() {
    scrollLockY = window.pageYOffset || document.documentElement.scrollTop || 0;
    document.body.style.position = 'fixed';
    document.body.style.top = -scrollLockY + 'px';
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.body.setAttribute('data-plps-locked', '');
  }

  function unlockScroll() {
    if (!document.body.hasAttribute('data-plps-locked')) return;
    document.body.removeAttribute('data-plps-locked');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    /* Jump straight back. html has scroll-behavior: smooth !important (merged.css), which made the page
       visibly scroll down from the top on every close; an inline !important wins over the stylesheet. */
    var html = document.documentElement;
    var prevBehavior = html.style.getPropertyValue('scroll-behavior');
    var prevPriority = html.style.getPropertyPriority('scroll-behavior');
    html.style.setProperty('scroll-behavior', 'auto', 'important');
    window.scrollTo(0, scrollLockY);
    if (prevBehavior) html.style.setProperty('scroll-behavior', prevBehavior, prevPriority);
    else html.style.removeProperty('scroll-behavior');
  }

  function setBackgroundInert(on) {
    var kids = document.body.children;
    for (var i = 0; i < kids.length; i++) {
      var el = kids[i];
      if (el === overlay || el === modal) continue;
      if (on) {
        el.setAttribute('inert', '');
        el.setAttribute('aria-hidden', 'true');
        el.setAttribute('data-plps-inerted', '');
      } else if (el.hasAttribute('data-plps-inerted')) {
        el.removeAttribute('inert');
        el.removeAttribute('aria-hidden');
        el.removeAttribute('data-plps-inerted');
      }
    }
  }

  function clearBackgroundInert() {
    var marked = document.querySelectorAll('[data-plps-inerted]');
    for (var i = 0; i < marked.length; i++) {
      marked[i].removeAttribute('inert');
      marked[i].removeAttribute('aria-hidden');
      marked[i].removeAttribute('data-plps-inerted');
    }
  }

  function labelModalFromStep(stepEl) {
    if (!modal) return;
    var title = (stepEl || modal).querySelector('.plps-title');
    if (!title) return;
    if (!title.id) { titleSeq += 1; title.id = 'plps-title-' + titleSeq; }
    modal.setAttribute('aria-labelledby', title.id);
    modal.removeAttribute('aria-label');
  }

  function buildModal(unit) {
    var tpl = root.querySelector('[data-plps-unit="' + unit + '"]');
    if (!tpl) return false;
    // Safety: never allow stacked overlays/modals (e.g. rapid editor selects).
    document.querySelectorAll('.plps-overlay, .plps-modal').forEach(function (el) { el.remove(); });
    clearBackgroundInert();
    overlay = document.createElement('div');
    overlay.className = 'plps-overlay';
    modal = document.createElement('div');
    modal.className = 'plps-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', 'Perfect Locks offer');
    modal.setAttribute('tabindex', '-1');
    // Which unit is open. Only the template's CONTENTS get cloned, so the
    // data-plps-unit attribute on <template> never reaches the DOM - without
    // this there is no way to style one unit differently from another.
    modal.setAttribute('data-plps-unit-type', unit);
    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'plps-close';
    close.setAttribute('aria-label', 'Close');
    close.setAttribute('data-plps-action', 'dismiss');
    var closeColor = cfg.units[unit] && cfg.units[unit].closeColor;
    close.style.setProperty('color', closeColor === 'white' ? '#FAF7F2' : '#1C1208', 'important');
    var iconTpl = root.querySelector('[data-plps-icon-close]');
    if (iconTpl) { close.appendChild(iconTpl.content.cloneNode(true)); }
    else { close.innerHTML = '&#10005;'; }
    modal.appendChild(close);
    modal.appendChild(tpl.content.cloneNode(true));
    /* Honeypot. A field no human can see or reach - hidden from assistive
       tech, out of the tab order, offscreen, autocomplete off - that form
       bots fill anyway because it is there. A filled honeypot marks the
       submit as automated. Injected at build time so every unit's forms get
       it without touching any template. */
    modal.querySelectorAll('form[data-plps-form]').forEach(function (f) {
      if (f.querySelector('input[name="website"]')) return;
      var hp = document.createElement('input');
      hp.type = 'text';
      hp.name = 'website';
      hp.setAttribute('tabindex', '-1');
      hp.setAttribute('aria-hidden', 'true');
      hp.setAttribute('autocomplete', 'off');
      hp.style.cssText = 'position:absolute;left:-9999px;width:1px;height:1px;opacity:0;pointer-events:none;';
      f.appendChild(hp);
    });
    document.body.appendChild(overlay);
    document.body.appendChild(modal);
    return true;
  }

  function openUnit(unit, opts) {
    if (state.open) return;
    lastFocus = document.activeElement;
    if (!buildModal(unit)) return;
    state.unit = unit;
    state.open = true;
    state.openedAt = Date.now();
    state.texture = ''; state.textureUrl = '';
    if (unit === 'cart_exit') hydrateCartFields();
    if (unit === 'new_visitor' && modal.querySelector('[data-plps-step="match"]')) {
      state.arm = welcomeArm();
      state.variant = (String(state.variant || 'A_new_visitor').split('|')[0]) + '|' + state.arm;
      if (state.arm === 'match') {
        setInitialStep('match');
        /* On a curly, wavy or straight page the texture is already known, so
           B opens on that texture's matches. "Change texture" still goes back
           to the question. ?pl_texture= forces it for previews. */
        var pageTex = params.get('pl_texture') || cfg.pageTexture || '';
        var texBtn = pageTex && modal.querySelector('[data-plps-step="match"] [data-plps-action="texture"][data-texture="' + pageTex + '"]');
        if (texBtn) {
          setInitialStep(applyTexture(texBtn));
          track('texture', { texture: state.texture, source: 'page' });
        }
      }
      modal.querySelectorAll('[data-plps-arm]').forEach(function (el) {
        if (el.getAttribute('data-plps-arm') !== state.arm) el.hidden = true;
      });
    } else {
      state.arm = '';
    }
    lockScroll();
    setBackgroundInert(true);
    /* Style gate. pl-popup-system.css loads non-blocking (media=print swap),
       so an early open - preview links, exit intent on a fast bounce, a slow
       phone - can build the modal before the stylesheet has applied. When
       the sheet then lands, the modal jumps to left/top 50% and its
       transform transition animates in from "none", which reads as the
       popup sliding in from the bottom right. Holding the modal invisible
       with transitions off until the sheet is live removes that: the first
       thing anyone sees is the intended fade. */
    var openingModal = modal, openingOverlay = overlay;
    var openTrigger = (opts && opts.trigger) || 'gate';
    openingModal.style.visibility = 'hidden';
    openingModal.style.transition = 'none';
    openingOverlay.style.transition = 'none';
    var stylesReady = new Promise(function (resolve) {
      function live() {
        try { return window.getComputedStyle(openingModal).position === 'fixed'; } catch (e) { return true; }
      }
      if (live()) { resolve(); return; }
      var waited = 0;
      (function poll() {
        if (live() || waited >= 3000) { resolve(); return; }
        waited += 50;
        setTimeout(poll, 50);
      })();
    }).then(function () {
      if (modal !== openingModal) return false;
      void openingModal.offsetWidth; // commit the closed state with transitions off
      openingModal.style.transition = '';
      openingOverlay.style.transition = '';
      openingModal.style.visibility = '';
      if (unit === 'game') { if (gameScratchFirst()) gameActivate(); else gamePrime(); }
      return true;
    });
    /* Reveal gate. Template content is inert, so an instant open (preview,
       exit intent, the showroom swap) starts every image cold and the modal
       used to paint before its hero arrived - the pop-in. Hold the reveal for
       image decode, capped at 400ms so a slow network can never make the
       popup feel slow; anything still loading after the cap fades in over
       the placeholder surface instead of popping from blank. */
    var gateImgs = Array.prototype.slice.call(
      modal.querySelectorAll('.plps-media img, .plps-bgimg img, .plps-game-stagebg img, .plps-cartline img')
    );
    gateImgs.forEach(function (im) {
      if (im.complete) { im.classList.add('plps-img-in'); return; }
      im.addEventListener('load', function () { im.classList.add('plps-img-in'); }, { once: true });
      im.addEventListener('error', function () { im.classList.add('plps-img-in'); }, { once: true });
    });
    var pending = gateImgs.filter(function (im) { return !im.complete; });
    var revealed = false;
    function reveal() {
      if (revealed) return;
      revealed = true;
      stylesReady.then(function (ok) {
        if (!ok) return;
        requestAnimationFrame(function () {
          if (modal !== openingModal) return;
          openingOverlay.classList.add('is-open');
          openingModal.classList.add('is-open');
          /* PL 2026-10-25: the impression now fires when the popup is actually
             on screen, not when it is built (a build that never reveals - a
             stylesheet that never loads, a modal replaced mid-open - no longer
             counts). view_unique fires once per unit per session and is the
             clean denominator for conversion rates and the v2 A/B test. */
          track('impression', { trigger: openTrigger });
          var vuKey = 'plps:vu:' + unit;
          var seen = false;
          try { seen = sessionStorage.getItem(vuKey) === '1'; sessionStorage.setItem(vuKey, '1'); } catch (err) {}
          if (!seen) track('view_unique', { trigger: openTrigger });
          // Focus could not land while the modal was hidden; place it now.
          if (!openingModal.contains(document.activeElement)) openingModal.focus();
        });
      });
    }
    if (!pending.length) {
      reveal();
    } else {
      setTimeout(reveal, 400);
      Promise.all(pending.map(function (im) {
        return im.decode ? im.decode().catch(function () {}) : Promise.resolve();
      })).then(reveal, reveal);
    }
    labelModalFromStep(modal.querySelector('[data-plps-step].is-active'));
    // Focus the dialog itself rather than the first input: avoids launching
    // the mobile keyboard on open and still announces the dialog.
    modal.focus();
    if (teaser) teaser.classList.remove('is-visible');
    hidePill(false);
    if (!isPreview && !inDesignMode) LS.set('last_open', String(Date.now()));
    document.addEventListener('keydown', onKeydown);
    modal.addEventListener('click', onModalClick);
    modal.addEventListener('submit', onSubmit);
    overlay.addEventListener('click', function () { dismiss(); });
  }

  function closeModal() {
    if (!state.open) return;
    state.open = false;
    document.removeEventListener('keydown', onKeydown);
    if (overlay) overlay.remove();
    if (modal) modal.remove();
    overlay = modal = null;
    setBackgroundInert(false);
    clearBackgroundInert();
    unlockScroll();
    if (lastFocus && document.contains(lastFocus) && typeof lastFocus.focus === 'function') {
      lastFocus.focus();
    }
    lastFocus = null;
  }

  function dismiss() {
    track('dismiss');
    var closedUnit = state.unit, closedVariant = state.variant;
    if (!isPreview && !inDesignMode) suppress(state.unit);
    closeModal();
    if (pillAllowed(closedUnit)) {
      showPill(closedUnit, closedVariant);
      track('pill_show');
    }
  }

  function showStep(name) {
    var steps = modal.querySelectorAll('[data-plps-step]');
    for (var i = 0; i < steps.length; i++) steps[i].classList.remove('is-active');
    var next = modal.querySelector('[data-plps-step="' + name + '"]');
    if (next) {
      next.classList.add('is-active');
      labelModalFromStep(next);
      var f = next.querySelector('input, button, a');
      if (f) f.focus();
      track('step', { step: name });
    }
  }

  /* ---------- inline errors ---------- */
  function clearError(form) {
    var box = form.querySelector('[data-plps-error]');
    if (box) { box.textContent = ''; box.hidden = true; }
    var field = form.querySelector('.plps-field');
    if (field) field.removeAttribute('aria-invalid');
  }

  function showError(form, message) {
    var box = form.querySelector('[data-plps-error]');
    var field = form.querySelector('.plps-field');
    if (field) {
      field.setAttribute('aria-invalid', 'true');
      try { field.focus(); } catch (e) {}
    }
    if (box) {
      box.hidden = false;
      box.textContent = message;
    }
  }

  function onKeydown(e) {
    if (e.key === 'Escape') { dismiss(); return; }
    if (e.key === 'Tab' && modal) {
      // Focus trap - backstop for browsers without inert support.
      // Only real tab stops: skip the honeypot and anything else taken out with tabindex="-1".
      var items = modal.querySelectorAll('input:not([type="hidden"]):not([disabled]):not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), a[href]:not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])');
      var list = [];
      for (var i = 0; i < items.length; i++) {
        if (items[i].offsetParent !== null || items[i] === modal) list.push(items[i]);
      }
      if (!list.length) return;
      var first = list[0], last = list[list.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === modal)) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    }
  }

  function setCode(code) {
    var els = modal.querySelectorAll('[data-plps-code]');
    for (var i = 0; i < els.length; i++) els[i].textContent = code;
  }

  function hydrateCartFields() {
    getCart().then(function (cart) {
      state.cart = cart;
      if (!modal) return;
      var names = modal.querySelectorAll('[data-plps-cart-name]');
      for (var i = 0; i < names.length; i++) names[i].textContent = cart.firstItem || 'Your shade';
      /* PL 2026-10-12: fill every cart line (save step and success step). */
      var qtys = modal.querySelectorAll('[data-plps-cart-qty]');
      for (var q = 0; q < qtys.length; q++) qtys[q].textContent = cart.count ? (cart.count + (cart.count === 1 ? ' item' : ' items') + ' in your cart') : '';
      var imgs = modal.querySelectorAll('[data-plps-cart-image]');
      for (var m = 0; m < imgs.length; m++) {
        if (cart.image) { imgs[m].src = cart.image; } else { imgs[m].style.display = 'none'; }
      }
      /* Personalized headline: the returning unit converts at 20x the intro
         units by acknowledging the visitor, so the cart save names the shade
         they actually chose. Falls back to the generic headline whenever the
         cart has no usable name. */
      var ph = modal.querySelector('[data-plps-exit-headline][data-personalized]');
      if (ph && (cart.variantTitle || cart.productTitle || cart.firstItem)) {
        /* PL 2026-10-12: headline names the shade only; variant titles carry
           length after a slash ("Chocolate Brown (3) / 14\""). */
        var shade = (cart.variantTitle || '').split(' / ')[0].trim() || cart.productTitle || cart.firstItem || '';
        var product = cart.productTitle || cart.firstItem || shade;
        var line = ph.getAttribute('data-personalized')
          .replace('{shade}', shade)
          .replace('{product}', product);
        if (line.replace(/\s/g, '')) ph.textContent = line;
      }
    });
  }

  function onModalClick(e) {
    var btn = e.target.closest('[data-plps-action]');
    if (!btn) return;
    var action = btn.getAttribute('data-plps-action');

    if (action === 'dismiss') { dismiss(); }

    else if (action === 'stylist') {
      LS.set('stylist', '1');
      CK.set('stylist', '1', 365);
      isStylist = true;
      track('handraise');
      var proTpl = root.querySelector('[data-plps-unit="pro"]');
      closeModal();
      if (proTpl) openUnit('pro', { trigger: 'handraise' });
    }

    else if (action === 'not-stylist') {
      LS.set('stylist', '0');
      CK.set('stylist', '0', 365);
      isStylist = false;
      dismiss();
    }

    else if (action === 'who-me') {
      LS.set('stylist', '0');
      CK.set('stylist', '0', 365);
      isStylist = false;
      track('who', { who: 'me' });
      showStep('email');
    }

    else if (action === 'goal') {
      state.goal = btn.getAttribute('data-goal') || '';
      state.goalUrl = btn.getAttribute('data-url') || '';
      var echo = modal.querySelector('[data-plps-goal-echo]');
      if (echo) echo.textContent = (btn.querySelector('b') ? btn.querySelector('b').textContent : 'Good choice') + ' it is';
      track('goal', { goal: state.goal });
      showStep('email');
    }

    else if (action === 'texture') {
      var nextStep = applyTexture(btn);
      track('texture', { texture: state.texture, source: 'tap' });
      showStep(nextStep);
    }

    else if (action === 'redeem') {
      e.preventDefault();
      var url = btn.hasAttribute('data-plps-goal-url')
        ? (state.goalUrl || '/collections/all')
        : ((btn.hasAttribute('data-plps-texture-url') && state.textureUrl) || btn.getAttribute('data-url') || '/');
      var code = state.smsCode || (cfg.units[state.unit] && cfg.units[state.unit].codeEmail);
      if (btn.hasAttribute('data-plps-pick')) {
        track('pick_click', { pick: btn.getAttribute('data-plps-pick'), pick_slot: btn.getAttribute('data-plps-pick-slot') || '', texture: state.texture || '' });
      }
      track('redeem');
      if (!isPreview && !inDesignMode) suppress(state.unit);
      unlockScroll();
      /* Same rule as the handoff paths: never navigate in the same tick as
         the analytics push, or the redeem hit is lost in flight. */
      var redeemTo = code ? ('/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(url)) : url;
      setTimeout(function () { location.href = redeemTo; }, 250);
    }

    else if (action === 'apply-redirect') {
      e.preventDefault();
      var applyUrl = btn.getAttribute('data-url') || '/';
      var joiner = applyUrl.indexOf('?') > -1 ? '&' : '?';
      track('apply');
      if (!isPreview && !inDesignMode) suppress(state.unit);
      unlockScroll();
      var applyTo = applyUrl + (state.email ? joiner + 'email=' + encodeURIComponent(state.email) : '');
      setTimeout(function () { location.href = applyTo; }, 250);
    }

    else if (action === 'copy') {
      e.preventDefault();
      var chip = btn.closest('.plps-code');
      var codeEl = chip && chip.querySelector('[data-plps-code]');
      var label = btn.querySelector('[data-plps-copy-label]');
      var text = codeEl ? (codeEl.textContent || '').trim() : '';
      if (!text) return;
      var done = function () {
        btn.setAttribute('data-copied', '1');
        if (label) label.textContent = 'Copied';
        track('code_copy');
        setTimeout(function () {
          btn.removeAttribute('data-copied');
          if (label) label.textContent = 'Copy';
        }, 2400);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done).catch(function () { fallbackCopy(text, done); });
      } else {
        fallbackCopy(text, done);
      }
    }

    else if (action === 'back') {
      e.preventDefault();
      var target = btn.getAttribute('data-step');
      if (!target || !modal.querySelector('[data-plps-step="' + target + '"]')) return;
      // The goal is only sent to Klaviyo on submit, so clearing it here is safe
      // and stops a stale pick leaking into the profile if they change their mind.
      if (target === 'goal') { state.goal = ''; state.goalUrl = ''; }
      if (target === 'match' || target === 'texture') { state.texture = ''; state.textureUrl = ''; state.goalUrl = ''; }
      track('back', { step: target });
      showStep(target);
    }

    else if (action === 'skip-sms') {
      track('sms_skip');
      if (state.unit === 'returning') returningHandoff();
      else showStep('success');
    }

    else if (action === 'link') {
      track('cta');
      if (!isPreview && !inDesignMode) suppress(state.unit);
      unlockScroll();
      // natural navigation proceeds
    }
    else if (action === 'colormatch') {
      e.preventDefault();
      track('cta');
      closeModal();
      openColorMatch(btn.getAttribute('data-url') || btn.getAttribute('href') || '/pages/find-your-perfect-match');
    }
  }

  /* The color-match widget is mounted on the PDP and collection templates,
     which is where the returning unit fires. When its launcher is on the
     page, open the drawer right here and hand it the email the popup just
     captured - one continuous flow, nothing asked twice. When it is not
     (home, blog), navigate to the match page with the email carried in the
     URL. Returns true when the in-place drawer path was taken. */
  function openColorMatch(dest) {
    /* Detect the widget by its email field, which exists in every mode; the
       launcher button is removable via its own setting and cannot be the
       test. Opening goes through the widget's own pl-cmw:open event, the
       same API its launcher links use, carrying the email. The email in hand is
       what tells the widget to skip its intro, so fall back to whatever is
       still typed in the popup's field if state was never set. */
    var handEmail = state.email || '';
    if (!handEmail && modal) {
      var lastField = modal.querySelector('input[name="email"]');
      if (lastField && lastField.value) handEmail = String(lastField.value).trim();
    }
    if (document.querySelector('[data-cmw-email]')) {
      window.setTimeout(function () {
        document.dispatchEvent(new CustomEvent('pl-cmw:open', { detail: { email: handEmail } }));
      }, 60);
      return true;
    }
    var joiner = dest.indexOf('?') > -1 ? '&' : '?';
    var parts = ['cm_open=1'];
    if (handEmail) parts.push('email=' + encodeURIComponent(handEmail));
    var navTo = dest + joiner + parts.join('&');
    /* Same rule as the handoff path: never navigate in the same tick as the
       analytics events. An instant location change killed the returning
       unit's email_submit hits in flight - Klaviyo saw the captures, GA4
       saw zero. A quarter second is invisible next to a page load. */
    window.setTimeout(function () { window.location.href = navTo; }, 250);
    return false;
  }

  /* PL 2026-10-29: the returning unit now asks for a phone number between
     the email and the photo match. Whichever way the visitor leaves that
     step (texts on, or "Email is enough"), this runs the handoff the pitch
     button promised. Pages without the widget get the success screen,
     whose button navigates to the match page instead. */
  function returningHandoff() {
    if (document.querySelector('[data-cmw-open]')) {
      track('handoff');
      closeModal();
      openColorMatch((cfg.units.returning && cfg.units.returning.ctaUrl) || '/pages/find-your-perfect-match');
    } else {
      showStep('success');
    }
  }
  function returningAsksPhone() {
    return state.unit === 'returning' && !!modal && !!modal.querySelector('[data-plps-step="sms"]') && !hasSms();
  }

  /* clipboard API needs a secure context and can be blocked; this keeps the
     button working on http previews and older browsers. */
  function fallbackCopy(text, done) {
    try {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.cssText = 'position:fixed;top:0;left:-9999px;opacity:0;';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      done();
    } catch (err) {}
  }

  function onSubmit(e) {
    var form = e.target.closest('[data-plps-form]');
    if (!form) return;
    e.preventDefault();
    var kind = form.getAttribute('data-plps-form');
    var btn = form.querySelector('button[type="submit"]');
    clearError(form);

    /* Theme editor: advance steps visually, never call Klaviyo. */
    if (inDesignMode) {
      if (kind === 'email') {
        /* Preview never subscribes, but it must still behave like the real
           flow: keep the typed email so downstream handoffs carry it. */
        var pvEmail = form.querySelector('input[name="email"]');
        if (pvEmail && pvEmail.value) state.email = String(pvEmail.value).trim();
        if (state.unit === 'pro') showStep('success');
        else if (state.unit === 'game' && !gameScratchFirst() && modal.querySelector('[data-plps-step="scratch"]')) { showStep('scratch'); gameActivate(); }
        else if (state.unit === 'returning' && modal.querySelector('[data-plps-step="sms"]')) showStep('sms');
        else if (state.unit === 'returning' && document.querySelector('[data-cmw-email]')) {
          closeModal();
          openColorMatch((cfg.units.returning && cfg.units.returning.ctaUrl) || '/pages/find-your-perfect-match');
        }
        else if (modal.querySelector('[data-plps-step="sms"]')) showStep('sms');
        else showStep('success');
      } else {
        var previewCode = cfg.units[state.unit] && cfg.units[state.unit].codeSms;
        if (previewCode) setCode(previewCode);
        showStep('success');
      }
      return;
    }

    /* Automated-submit check. Two signals, either one is enough: the
       honeypot has a value, or the email form was submitted faster than a
       person can read a headline and type an address (under 1.5s from the
       modal opening). Fail silently - advance the steps as if it worked so
       the bot cannot tell it was filtered - but make no Klaviyo call. */
    var hpField = form.querySelector('input[name="website"]');
    var tooFast = kind === 'email' && state.openedAt && (Date.now() - state.openedAt) < 1500;
    if ((hpField && hpField.value) || tooFast) {
      track('bot_block', { kind: kind, reason: (hpField && hpField.value) ? 'honeypot' : 'timing' });
      if (kind === 'email') {
        if (modal.querySelector('[data-plps-step="sms"]')) showStep('sms');
        else showStep('success');
      } else {
        showStep('success');
      }
      return;
    }

    if (kind === 'email') {
      var emailRaw = (form.querySelector('input[name="email"]') || {}).value || '';
      var email = String(emailRaw).trim();
      if (!validEmail(email)) {
        showError(form, 'That email address does not look right. Please check it and try again.');
        track('validation_error', { field: 'email' });
        return;
      }
      if (btn) { btn.disabled = true; btn.setAttribute('aria-busy', 'true'); }
      state.email = email;
      var props = { popup_unit: state.unit, popup_variant: state.variant || '' };
      // The exact code this visitor was handed on screen. The Klaviyo welcome
      // flow branches on popup_unit and merge-tags popup_code, so the number in
      // the email always matches the number in the popup. Without this the flow
      // has to mint its own code and the two contradict each other.
      var emailCode = cfg.units[state.unit] && cfg.units[state.unit].codeEmail;
      // welcome_code always records the code shown. popup_code is only set for
      // MATCHED codes, because the live welcome flow builds a dollar amount from
      // popup_code|slice:'7:'. Any other code (PERFECT10 from Oct 2026) clears it
      // so every template falls through to its 10%, no-minimum PERFECT10 branch.
      if (emailCode) {
        props.welcome_code = emailCode;
        props.popup_code = /^MATCHED/.test(emailCode) ? emailCode : '';
      }
      if (state.goal) props.hair_goal = state.goal;
      if (state.texture) props.hair_texture = state.texture;
      if (state.unit === 'pro') props.is_pro_prospect = true;
      // Give the abandonment flow something to name. Without these the email
      // cannot reference the shade the visitor actually left behind.
      if (state.unit === 'cart_exit' && state.cart) {
        if (state.cart.productTitle) props.cart_product = state.cart.productTitle;
        if (state.cart.variantTitle) props.cart_shade = state.cart.variantTitle;
        if (state.cart.variantId) props.cart_variant_id = state.cart.variantId;
        if (state.cart.url) props.cart_url = state.cart.url;
        if (state.cart.count) props.cart_item_count = state.cart.count;
      }

      consentOk({ ignoreBanner: true }).then(function (allowed) {
        if (!allowed) throw new Error('consent');
        return subscribe(cfg.emailListId, { email: email }, props, 'Popup ' + state.unit);
      }).then(function () {
        markSubscribed();
        klaviyoIdentify({ email: email });
        if (state.unit === 'cart_exit' && state.cart && state.cart.variantId) {
          backInStock(state.cart.variantId, { email: email }, ['EMAIL']);
        }
        track('email_submit');
        stampCart();
        if (state.goal) {
          klaviyoEvent('Popup Goal Selected', email, { goal: state.goal, unit: state.unit });
        }
        if (btn) { btn.disabled = false; btn.removeAttribute('aria-busy'); }
        // A form carrying data-plps-redirect hands off to another page instead of
        // advancing a step. Used by the returning unit, which captures the email
        // and then sends the visitor to the colour-match uploader with it
        // prefilled. The profile is created either way, so a visitor who never
        // finishes the upload is no longer lost entirely.
        var handoff = form.getAttribute('data-plps-redirect');
        if (handoff) {
          track('handoff');
          var sep = handoff.indexOf('?') > -1 ? '&' : '?';
          var dest = handoff + sep + 'email=' + encodeURIComponent(email);
          /* Navigation one beat later, not immediately: an instant
             location change can cancel the analytics hits in flight, which
             made handoff conversions invisible in GA4 while Klaviyo showed
             them fine. A quarter second is imperceptible next to a page
             load and lets the events clear. */
          setTimeout(function () { window.location.href = dest; }, 250);
          return;
        }
        if (state.unit === 'pro') showStep('success');
        else if (state.unit === 'game' && !gameScratchFirst() && modal.querySelector('[data-plps-step="scratch"]')) { showStep('scratch'); gameActivate(); }
        else if (returningAsksPhone()) showStep('sms');
        else if (state.unit === 'returning' && document.querySelector('[data-cmw-open]')) {
          /* The pitch this visitor just accepted IS the photo flow, so keep
             the promise immediately: close the popup and open the drawer
             with their email already in it. Pages without the widget fall
             through to the success screen, whose button navigates instead. */
          track('handoff');
          closeModal();
          openColorMatch((cfg.units.returning && cfg.units.returning.ctaUrl) || '/pages/find-your-perfect-match');
        }
        else if (modal.querySelector('[data-plps-step="sms"]')) showStep('sms');
        else showStep('success');
      }).catch(function (err) {
        if (btn) { btn.disabled = false; btn.removeAttribute('aria-busy'); }
        track('submit_error', { kind: 'email', reason: (err && err.message) || 'unknown' });
        showError(form, 'We could not save that just now. Please try again in a moment.');
      });
    }

    if (kind === 'sms') {
      var phoneRaw = (form.querySelector('input[name="phone"]') || {}).value || '';
      var phone = normalizePhone(phoneRaw);
      if (!phone) {
        showError(form, 'Please enter a valid mobile number, for example (510) 555-1234.');
        track('validation_error', { field: 'phone' });
        return;
      }
      if (btn) { btn.disabled = true; btn.setAttribute('aria-busy', 'true'); }
      var smsProps = { popup_unit: state.unit, popup_variant: state.variant || '' };
      // Overwrites popup_code from the email step. Reaching this step means the
      // visitor upgraded, so the SMS-tier code is the one they were promised.
      var smsTierCode = cfg.units[state.unit] && cfg.units[state.unit].codeSms;
      if (smsTierCode) {
        smsProps.welcome_code = smsTierCode;
        smsProps.popup_code = /^MATCHED/.test(smsTierCode) ? smsTierCode : '';
      }
      if (state.unit === 'cart_exit') {
        smsProps.shade_watch = true;
        if (state.cart && state.cart.variantId) smsProps.cart_variant_id = state.cart.variantId;
        if (state.cart && state.cart.productTitle) smsProps.cart_product = state.cart.productTitle;
      }

      consentOk({ ignoreBanner: true }).then(function (allowed) {
        if (!allowed) throw new Error('consent');
        return subscribe(cfg.smsListId, { email: state.email, phone: phone }, smsProps, 'Popup ' + state.unit + ' SMS');
      }).then(function () {
        klaviyoIdentify({ email: state.email, phone: phone });
        if (state.unit === 'cart_exit' && state.cart && state.cart.variantId) {
          backInStock(state.cart.variantId, { email: state.email, phone: phone }, ['EMAIL', 'SMS']);
        }
        track('sms_submit');
        markSms();
        if (btn) { btn.disabled = false; btn.removeAttribute('aria-busy'); }
        var smsCode = cfg.units[state.unit] && cfg.units[state.unit].codeSms;
        if (smsCode) { state.smsCode = smsCode; setCode(smsCode); }
        stampCart();
        if (state.unit === 'returning') returningHandoff();
        else showStep('success');
      }).catch(function (err) {
        if (btn) { btn.disabled = false; btn.removeAttribute('aria-busy'); }
        track('submit_error', { kind: 'sms', reason: (err && err.message) || 'unknown' });
        showError(form, 'We could not sign you up for texts just now. Your email code is still good - close this and check your inbox.');
      });
    }
  }

  /* ---------- Unit 7: scratch game ---------- */
  /* The card ships inert: cream prize face, canvas foil on top, lock overlay
     explaining the email gate. gameActivate() runs after the email submit -
     it lifts the lock, paints the foil, and wires the scratch. Painting waits
     for a frame because the scratch step has only just become visible and the
     canvas needs real layout dimensions. "Reveal it for me" is the keyboard,
     switch and screen-reader path and also catches impatient thumbs; both
     routes land in the same complete() so the prize never depends on motor
     ability. Clearing 55% of the foil counts as done - nobody should have to
     sand the whole card. */
  var gameRevealed = false;
  /* PL 2026-10-20: "Scratch first" order (Alia's exit spin converted 6.2% vs
     3.4% for email-first). The card opens unlocked; the reveal leads to the
     email step to claim the prize. Set per block in the editor. */
  function gameScratchFirst() {
    return !!(modal && modal.querySelector('[data-plps-game-order="scratch_first"]'));
  }
  function gamePaintFoil(canvas, ctx, bandPos, withText) {
    var r = canvas.getBoundingClientRect();
    if (!r.width || !r.height) return;
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var pw = Math.round(r.width * dpr);
    if (canvas.width !== pw) { canvas.width = pw; canvas.height = Math.round(r.height * dpr); }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    var w = r.width, h = r.height;
    var g = ctx.createLinearGradient(0, 0, w, h * 0.9);
    g.addColorStop(0, '#CFA265');
    g.addColorStop(0.28, '#B8874A');
    g.addColorStop(0.5, '#E2C48E');
    g.addColorStop(0.62, '#A97C40');
    g.addColorStop(0.85, '#C89A5C');
    g.addColorStop(1, '#B8874A');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);
    var seed = 9;
    function rnd() { seed = (seed * 16807) % 2147483647; return seed / 2147483647; }
    ctx.lineWidth = 1;
    for (var i = 0; i < 90; i++) {
      var y0 = rnd() * (h + 40) - 20;
      var x0 = rnd() * w;
      var len = 30 + rnd() * 90;
      var tone = rnd();
      ctx.strokeStyle = tone < 0.5
        ? 'rgba(255,240,210,' + (0.05 + tone * 0.08).toFixed(3) + ')'
        : 'rgba(90,62,25,' + (0.04 + (tone - 0.5) * 0.08).toFixed(3) + ')';
      ctx.beginPath();
      ctx.moveTo(x0, y0);
      ctx.lineTo(x0 + len, y0 - len * 0.18);
      ctx.stroke();
    }
    if (bandPos !== undefined) {
      var bx = bandPos * (w + h);
      var sg = ctx.createLinearGradient(bx - 70, 0, bx + 70, h * 0.35);
      sg.addColorStop(0, 'rgba(255,248,230,0)');
      sg.addColorStop(0.5, 'rgba(255,248,230,0.28)');
      sg.addColorStop(1, 'rgba(255,248,230,0)');
      ctx.fillStyle = sg;
      ctx.fillRect(0, 0, w, h);
    }
    /* The engraving only belongs on a card that can actually be scratched.
       The locked state paints bare gold - the lock overlay carries the one
       message that matters there. */
    if (withText !== false) {
      ctx.textAlign = 'center';
      ctx.font = '600 12px "Brandon Grotesque", sans-serif';
      ctx.fillStyle = 'rgba(255,240,210,0.85)';
      ctx.fillText('S C R A T C H   T O   R E V E A L', w / 2, h / 2 + 5);
      ctx.fillStyle = '#6E521F';
      ctx.fillText('S C R A T C H   T O   R E V E A L', w / 2, h / 2 + 4);
    }
  }
  /* Paint the foil the moment the unit opens, while the card is still locked.
     Without this the canvas is transparent on the email step and the prize
     face shows straight through the lock scrim - the reveal spoiled before
     the email is given, and the lock text tangled with the prize text. A
     locked GOLD card is also simply the promise the headline makes. */
  function gamePrime() {
    if (!modal || state.unit !== 'game') return;
    var canvas = modal.querySelector('[data-plps-game-foil]');
    if (!canvas || !canvas.getContext) return;
    var ctx = canvas.getContext('2d');
    requestAnimationFrame(function () { gamePaintFoil(canvas, ctx, undefined, false); });
  }
  function gameActivate() {
    if (!modal || state.unit !== 'game') return;
    gameRevealed = false;
    var canvas = modal.querySelector('[data-plps-game-foil]');
    var lock = modal.querySelector('[data-plps-game-lock]');
    var hint = modal.querySelector('[data-plps-game-hint]');
    var revealBtn = modal.querySelector('[data-plps-game-reveal]');
    var live = modal.querySelector('[data-plps-game-live]');
    if (!canvas || !canvas.getContext) {
      if (gameScratchFirst()) showStep('email');
      else if (modal.querySelector('[data-plps-step="sms"]')) showStep('sms');
      else showStep('success');
      return;
    }
    var ctx = canvas.getContext('2d');
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (lock) lock.hidden = true;
    if (revealBtn) revealBtn.hidden = false;
    if (hint) hint.textContent = 'Scratch the card to reveal your offer.';
    if (live) live.textContent = gameScratchFirst()
      ? 'Scratch the card, or use the Reveal it for me button.'
      : 'Card unlocked. Scratch the card, or use the Reveal it for me button.';
    /* showStep found nothing to focus in the scratch panel - its interactive
       elements live in the stage column. Focus the reveal button so keyboard
       users land on the accessible path, and so the browser scrolls the card
       into view on stacked mobile, where it sits above the form. */
    if (revealBtn) { try { revealBtn.focus(); } catch (e) {} }
    var cardEl = canvas.closest ? canvas.closest('.plps-game-card') : null;
    if (cardEl) cardEl.classList.add('is-armed');

    /* Foil rendering. Brushed diagonal strands over a six-stop gradient read
       as metal rather than flat ochre; the lettering is drawn twice, light
       pass then ink pass, for an engraved look. Until the first touch a
       specular band sweeps the card - the shimmer is the invitation - then
       stops forever, because each shimmer frame repaints the whole foil and
       would heal any scratches. The strand pattern is seeded, not random, so
       shimmer repaints are stable instead of crawling. */
    var interacted = false;
    var shimmerPos = -0.35;
    var shimmerRaf = 0;
    function drawFoil(bandPos) { gamePaintFoil(canvas, ctx, bandPos); }
    function shimmer() {
      if (interacted || gameRevealed) return;
      shimmerPos += 0.008;
      if (shimmerPos > 1.35) shimmerPos = -0.35;
      drawFoil(shimmerPos);
      shimmerRaf = requestAnimationFrame(shimmer);
    }
    requestAnimationFrame(function () {
      drawFoil();
      if (!reduced) shimmerRaf = requestAnimationFrame(shimmer);
    });

    var scratching = false;
    var strokes = 0;
    function scratchAt(x, y) {
      var r = canvas.getBoundingClientRect();
      var px = x - r.left, py = y - r.top;
      /* Feathered eraser: solid core, soft rim. Reads as a thumb rubbing
         foil away rather than a cursor punching holes. */
      var brush = ctx.createRadialGradient(px, py, 6, px, py, 26);
      brush.addColorStop(0, 'rgba(0,0,0,1)');
      brush.addColorStop(0.7, 'rgba(0,0,0,1)');
      brush.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.globalCompositeOperation = 'destination-out';
      ctx.fillStyle = brush;
      ctx.beginPath();
      ctx.arc(px, py, 26, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalCompositeOperation = 'source-over';
      strokes++;
      if (strokes % 6 === 0) checkCleared();
    }
    /* Counts a sample as cleared once its foil is mostly gone, not only when
       it is perfectly transparent: the feathered brush leaves a soft rim of
       partial alpha that a strict zero test never counted, so a card that
       looked well scratched could sit just under the line forever. The test
       also runs on pointerup (below), so the last few strokes of a scratch
       are never left unchecked. 45% is plenty - the prize is legible well
       before that. */
    function checkCleared() {
      var img;
      try { img = ctx.getImageData(0, 0, canvas.width, canvas.height).data; } catch (e) { return; }
      var clear = 0, total = 0;
      for (var i = 3; i < img.length; i += 96) {
        total++;
        if (img[i] < 128) clear++;
      }
      if (total > 0 && clear / total >= 0.45) complete();
    }
    function complete() {
      if (gameRevealed) return;
      gameRevealed = true;
      canvas.classList.add('is-cleared');
      if (reduced) canvas.style.display = 'none';
      if (hint) hint.textContent = '';
      if (revealBtn) revealBtn.hidden = true;
      if (live) live.textContent = 'Offer revealed.';
      track('game_reveal');
      setTimeout(function () {
        if (!modal) return;
        if (gameScratchFirst()) showStep('email');
        else if (modal.querySelector('[data-plps-step="sms"]')) showStep('sms');
        else showStep('success');
      }, reduced ? 0 : 650);
    }

    canvas.addEventListener('pointerdown', function (e) {
      if (gameRevealed) return;
      if (!interacted) {
        interacted = true;
        if (shimmerRaf) cancelAnimationFrame(shimmerRaf);
        drawFoil();
      }
      if (navigator.vibrate) { try { navigator.vibrate(8); } catch (err) {} }
      scratching = true;
      try { canvas.setPointerCapture(e.pointerId); } catch (err) {}
      scratchAt(e.clientX, e.clientY);
    });
    canvas.addEventListener('pointermove', function (e) {
      if (scratching && !gameRevealed) scratchAt(e.clientX, e.clientY);
    });
    canvas.addEventListener('pointerup', function () {
      scratching = false;
      if (!gameRevealed) checkCleared();
    });
    canvas.addEventListener('pointercancel', function () {
      scratching = false;
      if (!gameRevealed) checkCleared();
    });
    if (revealBtn) revealBtn.addEventListener('click', complete);
  }

  /* ---------- Unit 8: showroom geo gate ---------- */
  /* Optional endpoint (a Cloudflare Worker reading CDN geo headers) returning
     {"show": true}. Blank endpoint means no geo restriction. Fails closed on
     error or a slow response: a wrong-region invite costs trust, a missed
     one costs nothing - the subscriber slot was empty before this unit. */
  var geoCache = null;
  function showroomGeoOk() {
    var u = cfg.units.showroom && cfg.units.showroom.geoUrl;
    if (!u) return Promise.resolve(true);
    if (geoCache) return geoCache;
    geoCache = new Promise(function (resolve) {
      var done = false;
      var t = setTimeout(function () { if (!done) { done = true; resolve(false); } }, 1500);
      try {
        fetch(u, { credentials: 'omit' }).then(function (r) { return r.json(); }).then(function (j) {
          if (!done) { done = true; clearTimeout(t); resolve(!!(j && j.show)); }
        }).catch(function () { if (!done) { done = true; clearTimeout(t); resolve(false); } });
      } catch (e) {
        if (!done) { done = true; clearTimeout(t); resolve(false); }
      }
    });
    return geoCache;
  }

  /* ---------- subscriber slot ---------- */
  function smsUpgradeOk() {
    return !!root.querySelector('[data-plps-unit="sms_upgrade"]') && !!cfg.units.sms_upgrade &&
      pathAllowed('sms_upgrade') && utmAllowed('sms_upgrade') && !suppressed('sms_upgrade') &&
      !cfg.knownCustomer && !hasSms() && sessions >= 2;
  }
  function showroomOk() {
    return !!root.querySelector('[data-plps-unit="showroom"]') && !!cfg.units.showroom &&
      pathAllowed('showroom') && utmAllowed('showroom') && !suppressed('showroom');
  }
  function subscriberUnit() {
    if (smsUpgradeOk()) return 'sms_upgrade';
    if (showroomOk()) return 'showroom';
    return null;
  }

  /* ---------- targeting: which unit does this visitor get? ---------- */
  function pickGateUnit() {
    var has = function (u) {
      return !!root.querySelector('[data-plps-unit="' + u + '"]') &&
        !!cfg.units[u] &&
        pathAllowed(u) &&
        utmAllowed(u);
    };

    /* Promo takeover: while the promo unit is enabled and not past its end
       date, it wins the gate slot for everyone - subscribers and stylists
       included - so Units 1-4 stand down without touching their toggles.
       Cart exit is armed separately and keeps running. */
    if (has('promo') && !suppressed('promo') && !promoExpired()) {
      state.variant = 'promo';
      return 'promo';
    }

    if (isStylist && has('pro') && !suppressed('pro')) { state.variant = 'pro'; return 'pro'; }
    if (isSubscribed) {
      /* The subscriber slot. Every welcome unit suppresses known subscribers.
         PL 2026-10-13: the SMS upgrade (Unit 9) asks only for the one thing
         they have not given yet and wins the slot; the showroom invite asks
         for nothing and follows. */
      var subUnit = subscriberUnit();
      if (subUnit) { state.variant = subUnit; return subUnit; }
      return null;
    }

    /* PL 2026-10-13 Offer-first routing (Alia learning). Every first-session
       visitor gets Unit 1. From the second session, visitors who still have
       not signed up get the quiz (or the photo-match unit), which is where
       Alia's one-question survey converted at 8%. */
    if (cfg.introRouting !== 'ab') {
      var minS = (cfg.units.returning && cfg.units.returning.minSessions) || 2;
      if (minS < 2) minS = 2;
      if (sessions >= minS) {
        var order = cfg.returningSlot === 'returning' ? ['returning', 'quiz'] : ['quiz', 'returning'];
        for (var r = 0; r < order.length; r++) {
          if (has(order[r]) && !suppressed(order[r])) {
            state.variant = order[r] === 'quiz' ? 'quiz_returning' : 'returning';
            return order[r];
          }
        }
      }
      if (has('new_visitor') && !suppressed('new_visitor')) {
        state.variant = 'A_new_visitor';
        return 'new_visitor';
      }
      return null;
    }

    if (has('returning') && !suppressed('returning') &&
        sessions >= (cfg.units.returning.minSessions || 2) && LS.get('seen_intro') === '1') {
      state.variant = 'returning';
      return 'returning';
    }

    /* Intro roll. Deterministic on the visitor id so the same person always
       lands in the same arm. Two arms honour the A/B slider; the moment the
       scratch game is enabled the roll becomes equal thirds and the slider is
       ignored - a three-way test with a weighted two-way knob would silently
       misallocate traffic. */
    var hash = 0;
    for (var i = 0; i < visitorId.length; i++) hash = (hash * 31 + visitorId.charCodeAt(i)) >>> 0;

    var arms = [];
    if (has('new_visitor')) arms.push({ unit: 'new_visitor', variant: 'A_new_visitor' });
    if (has('quiz')) arms.push({ unit: 'quiz', variant: 'B_quiz' });
    /* PL 2026-10-13: a game rendered only for the exit offer is not a test arm. */
    var gameArm = has('game') && !(cfg.units.game && cfg.units.game.exitOnly);
    if (gameArm) arms.push({ unit: 'game', variant: 'C_game' });
    if (!arms.length) return null;

    var pick;
    if (arms.length === 2 && !gameArm) {
      pick = (hash % 100) < cfg.abSplitPercent ? arms[0] : arms[1];
    } else {
      pick = arms[hash % arms.length];
    }
    if (has(pick.unit) && !suppressed(pick.unit)) {
      state.variant = pick.variant;
      return pick.unit;
    }
    return null;
  }

  /* ---------- engagement gate: first scroll + dwell ---------- */
  function armGate() {
    var unit = pickGateUnit();
    if (!unit) return;

    /* PL 2026-10-13: the first scroll + dwell still arms the unit, and a time
       fallback now also arms it for visitors who never scroll (Alia opened
       at 14s on any device, 20s on phones). Whichever comes first wins. */
    var gateFired = false;
    function fireGate() {
      if (gateFired) return;
      gateFired = true;
      whenHuman(openGate);
    }
    function openGate() {
      warmUnit(unit);
      var remaining = Math.max(0, cfg.dwellMs - performance.now());
      setTimeout(function () {
        if (state.open || suppressed(unit) || !pathAllowed(unit) || recentlyOpened()) return;
        Promise.all([consentOk(), identifiedPromise]).then(function (results) {
          var allowed = results[0];
          var identified = results[1];
          if (!allowed || state.open) return;
          whenOverlaysClear(function () {
          if (state.open || suppressed(unit) || !pathAllowed(unit) || cmpDismissed()) return;
          /* A known subscriber only ever qualifies for the pro unit or the
             promo takeover; every other unit is suppressed for them in
             pickGateUnit(). */
          if (identified && unit !== 'pro' && unit !== 'promo' && unit !== 'showroom' && unit !== 'sms_upgrade') {
            var sw = subscriberUnit();
            if (!sw) return;
            unit = sw;
            state.variant = sw;
            warmUnit(sw);
          }
          if (unit === 'new_visitor' || unit === 'quiz' || unit === 'game') LS.set('seen_intro', '1');
          if (unit === 'showroom') {
            showroomGeoOk().then(function (ok) {
              if (ok && !state.open) openUnit('showroom', { trigger: 'gate' });
            });
            return;
          }
          /* PL 2026-10-13: open the intro popup directly (Alia did); the teaser
             tab is only the reopen pill after a close. openDirect:false restores
             the old teaser-first behaviour. */
          if (cfg.openDirect === false && teaser && (unit === 'new_visitor' || unit === 'quiz' || unit === 'game')) {
            hidePill(false);
            teaser.hidden = false;
            teaser.classList.add('is-visible');
            teaser.addEventListener('click', function () { openUnit(unit, { trigger: 'teaser' }); }, { once: true });
            state.unit = unit; // attribute the teaser event before any modal exists
            track('teaser');
            if (cfg.autoOpenMs > 0) {
              setTimeout(function () {
                if (!state.open && teaser.classList.contains('is-visible')) openUnit(unit, { trigger: 'auto' });
              }, cfg.autoOpenMs);
            }
          } else {
            openUnit(unit, { trigger: 'gate' });
          }
          });
        });
      }, remaining);
    }
    window.addEventListener('scroll', fireGate, { once: true, passive: true });
    var isPhone = window.matchMedia && window.matchMedia('(max-width: 860px)').matches;
    var openAfter = isPhone ? cfg.openAfterMsMobile : cfg.openAfterMs;
    if (openAfter > 0) {
      setTimeout(fireGate, Math.max(0, openAfter - performance.now()));
    }
  }

  /* Images live inside <template>, so the browser treats them as inert and
     fetches nothing until the unit is cloned in. That keeps the system free on
     page load, at the cost of a visible pop-in the moment the modal opens.
     Warming the URL as soon as a unit becomes eligible closes that gap: the
     fetch happens during the dwell wait, so by the time it opens the image is
     already in cache. Called at most once per unit. */
  var warmed = {};
  function warmUnit(unit) {
    if (!unit || warmed[unit]) return;
    warmed[unit] = true;
    try {
      var tpl = root.querySelector('[data-plps-unit="' + unit + '"]');
      if (!tpl || !tpl.content) return;
      /* Clone the media into a live offscreen container instead of guessing
         URLs. The browser then runs its own source selection - media query,
         srcset, sizes, device pixel ratio - and fetches the exact file the
         modal's clone will request, so the cache hit is guaranteed. The old
         approach warmed the first srcset candidate (600w), which a 2x
         display never requests: it resolves the 320px slot to 640px and
         fetches the 1000w file, missing the cache on every retina desktop. */
      var nodes = tpl.content.querySelectorAll('picture, img');
      if (!nodes.length) return;
      var warmer = document.createElement('div');
      warmer.setAttribute('aria-hidden', 'true');
      warmer.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);pointer-events:none;';
      nodes.forEach(function (el) {
        if (el.tagName === 'IMG' && el.parentNode && el.parentNode.tagName === 'PICTURE') return;
        warmer.appendChild(el.cloneNode(true));
      });
      document.body.appendChild(warmer);
      setTimeout(function () {
        if (warmer.parentNode) warmer.parentNode.removeChild(warmer);
      }, 15000);
    } catch (e) {}
  }

  /* ---------- desktop exit intent for cart_exit ---------- */
  /* Something else already owns the screen: the mini-cart, a modal, a menu.
     The theme's OpenableElement marks these with an `open` attribute. Firing
     exit intent over an open drawer reads as an ambush, because the visitor
     is mid-task rather than leaving. */
  function overlayOpen() {
    if (document.querySelector('cart-drawer[open], [id="mini-cart"][open]')) return true;
    if (document.querySelector('.drawer[open], .modal[open], dialog[open]')) return true;
    // Match bar bottom sheets (mobile) - the visitor is mid-shade-picking.
    if (document.querySelector('.pl-mb-panel.is-open')) return true;
    return false;
  }

  /* Wait until no drawer/sheet is open, then run cb. Polls because the theme's
     overlays have no shared close event. Capped at 90s so an abandoned tab
     doesn't hold a timer forever; if still blocked by then, this pageview
     simply gets no popup - the next one starts fresh. */
  function whenOverlaysClear(cb) {
    var waited = 0;
    (function poll() {
      if (!overlayOpen()) { cb(); return; }
      waited += 1000;
      if (waited >= 90000) return;
      setTimeout(poll, 1000);
    })();
  }

  /* Adding to cart is the opposite of leaving. A mouseout in the seconds after
     it is almost always the pointer travelling to the drawer that just opened,
     so hold fire and let the visitor finish what they started. */
  var lastCartChange = 0;
  ['cart:updated', 'cart:refresh', 'variant:added', 'product:added'].forEach(function (evt) {
    document.addEventListener(evt, function () { lastCartChange = Date.now(); });
  });
  document.addEventListener('click', function (e) {
    var t = e.target && e.target.closest && e.target.closest('[name="add"], [data-action="add-to-cart"], form[action*="/cart/add"] [type="submit"]');
    if (t) lastCartChange = Date.now();
  }, true);
  var CART_GRACE_MS = 10000;

  /* PL 2026-10-13 site-wide exit offer (Alia learning). A visitor who has
     not signed up and leaves a page with an empty cart gets Unit 1, at most
     once every exitOfferDays, never within the popup gap. */
  /* PL 2026-10-13: exit offer runs on the scratch card (Alia's exit was a
     spin-to-win, email only, every spin 10%), falling back to the welcome
     popup if the card is not rendered. */
  function exitUnit() {
    var u = cfg.exitOfferUnit === 'game' ? 'game' : 'new_visitor';
    if (u === 'game' && !(root.querySelector('[data-plps-unit="game"]') && cfg.units.game)) u = 'new_visitor';
    return u;
  }
  function exitOfferOk() {
    var u = exitUnit();
    return !!cfg.exitOffer && !isSubscribed && !isStylist &&
      !!root.querySelector('[data-plps-unit="' + u + '"]') && !!cfg.units[u] &&
      pathAllowed(u) && utmAllowed(u) && !suppressed('exit_offer');
  }

  function armExitIntent() {
    var cartArmed = !!root.querySelector('[data-plps-unit="cart_exit"]') && !!cfg.units.cart_exit &&
      pathAllowed('cart_exit') && utmAllowed('cart_exit') && !suppressed('cart_exit');
    if (!cartArmed && !exitOfferOk()) return;
    var armedAt = Date.now();
    var fired = false;
    getCart().then(function (cart) {
      if (cart && cart.count && cartArmed) warmUnit('cart_exit');
      else if (exitOfferOk()) warmUnit(exitUnit());
    });

    function tryFire(trigger, cleanup) {
      if (fired || state.open || !humanSeen) return;
      if (overlayOpen()) return;
      if (Date.now() - lastCartChange < CART_GRACE_MS) return;
      if (Date.now() - armedAt < 5000) return;
      consentOk().then(function (allowed) {
        if (!allowed || fired || state.open) return;
        return getCart().then(function (cart) {
          if (fired || state.open) return;
          if (cart.count && cartArmed) {
            fired = true;
            if (cleanup) cleanup();
            state.variant = 'cart_exit';
            openUnit('cart_exit', { trigger: trigger });
            return;
          }
          if (!cart.count && exitOfferOk() && !recentlyOpened()) {
            fired = true;
            if (cleanup) cleanup();
            suppress('exit_offer');
            state.variant = 'exit_offer';
            openUnit(exitUnit(), { trigger: trigger });
          }
        });
      });
    }

    if (window.matchMedia('(pointer: fine)').matches) {
      var handler = function (e) {
        if (fired || state.open) return;
        if (e.clientY > 0 || e.relatedTarget) return;
        tryFire('exit', function () { document.removeEventListener('mouseout', handler); });
      };
      document.addEventListener('mouseout', handler);
      return;
    }

    /* Mobile back-intent. A fast flick toward the top after real browsing
       depth reads as reaching for the URL bar or the back gesture, which is
       the closest thing touch has to mouse-out. Thresholds are deliberately
       strict - 350px of upward travel inside half a second, only after 600px
       of depth, and never when already near the top - because a misfire here
       is an ambush mid-read, and an ambush costs more trust than the save is
       worth. One shot per page, same cart and consent guards as desktop. */
    var lastY = window.scrollY;
    var lastT = Date.now();
    var upDist = 0;
    var maxDepth = 0;
    var armed = false;
    var settleTimer = null;
    var onScroll = function () {
      if (fired || state.open) return;
      var y = window.scrollY;
      var t = Date.now();
      if (y > maxDepth) maxDepth = y;
      if (y < lastY) {
        if (t - lastT <= 500) upDist += lastY - y; else upDist = lastY - y;
      } else {
        upDist = 0;
      }
      lastY = y;
      lastT = t;
      if (upDist >= 350 && maxDepth >= 600 && y > 100) {
        upDist = 0;
        armed = true;
      }
      /* Open once the flick has settled, not mid-swipe: locking scroll under a moving finger
         feels like the page froze. */
      if (armed) {
        clearTimeout(settleTimer);
        settleTimer = setTimeout(function () {
          armed = false;
          if (fired || state.open) return;
          tryFire('exit_mobile', function () { window.removeEventListener('scroll', onScroll); });
        }, 180);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------- boot ---------- */
  if (inDesignMode) {
    /* Theme editor integration: selecting a unit block in the customizer opens
       that unit immediately - no gates, no suppression - and deselecting closes
       it. Forms advance through their steps on submit WITHOUT calling Klaviyo,
       so every screen can be reviewed by clicking through it. */
    document.addEventListener('shopify:block:select', function (e) {
      var tpl = e.target && (e.target.matches('[data-plps-unit]') ? e.target : e.target.querySelector('[data-plps-unit]'));
      if (!tpl) return;
      closeModal();
      editorUnit = tpl.getAttribute('data-plps-unit');
      openUnit(editorUnit, { trigger: 'editor' });
    });
    document.addEventListener('shopify:block:deselect', function (e) {
      var tpl = e.target && (e.target.matches('[data-plps-unit]') ? e.target : e.target.querySelector('[data-plps-unit]'));
      if (tpl) { closeModal(); editorUnit = null; }
    });
    document.addEventListener('shopify:section:load', function (e) {
      var fresh = e.target && (e.target.matches('[data-plps-root]') ? e.target : e.target.querySelector('[data-plps-root]'));
      if (!fresh) return;
      var freshCfg = fresh.querySelector('[data-plps-config]');
      try { if (freshCfg) cfg = JSON.parse(freshCfg.textContent); } catch (err) { /* keep the last good config */ }
      root = fresh;
      if (editorUnit && root.querySelector('[data-plps-unit="' + editorUnit + '"]')) {
        closeModal();
        openUnit(editorUnit, { trigger: 'editor' });
      }
    });
    document.addEventListener('shopify:section:unload', function () { closeModal(); });
  } else if (previewUnit && root.querySelector('[data-plps-unit="' + previewUnit + '"]')) {
    state.variant = 'preview';
    openUnit(previewUnit, { trigger: 'preview' });
  } else {
    /* The identified probe waits on Klaviyo's onsite script, which the theme
       loads lazily via requestIdleCallback, so it can take up to three
       seconds to answer. Arming the gate behind it meant the scroll listener
       attached that late, and because it is a `once` listener a first scroll
       inside that window landed on nothing and was lost - the visitor then
       had to scroll a second time to see anything. The listeners now attach
       synchronously and the probe is resolved inside the dwell wait, which
       already has a six second budget to spend.

       Deferring the probe does not change who sees what. pickGateUnit()
       suppresses every unit except `pro` for a known subscriber, so the only
       thing the answer can still do by the time the dwell elapses is cancel
       a non-pro unit, which is exactly what the check below does. */
    identifiedPromise = klaviyoIdentified().then(function (identified) {
      if (identified) markSubscribed();
      return identified;
    }).catch(function () { return false; });
    armGate();
    armExitIntent();

    if (pill) {
      var openBtn = pill.querySelector('[data-plps-pill-open]');
      var closeBtn = pill.querySelector('[data-plps-pill-close]');
      if (openBtn) openBtn.addEventListener('click', function () {
        var u = pill.getAttribute('data-unit');
        if (!u || state.open) return;
        state.variant = pill.getAttribute('data-variant') || state.variant;
        openUnit(u, { trigger: 'pill' });
      });
      if (closeBtn) closeBtn.addEventListener('click', function () {
        state.unit = pill.getAttribute('data-unit') || state.unit;
        track('pill_close');
        hidePill(true);
      });
      var saved = readPill();
      if (saved && saved.unit && pillAllowed(saved.unit) && pathAllowed(saved.unit)) {
        Promise.all([consentOk(), identifiedPromise]).then(function (r) {
          if (!r[0] || state.open) return;
          if (r[1] && saved.unit !== 'sms_upgrade') { hidePill(true); return; }
          if (pillAllowed(saved.unit)) showPill(saved.unit, saved.variant);
        });
      }
    }
  }

  // Safety net: never leave the page scroll-locked if something navigates away.
  window.addEventListener('pagehide', unlockScroll);
})();